import { FC } from "react";
import { FilterDescriptor } from "@app/network-portfolio-management/interfaces";
import { Badge } from "@app/shared/Badge";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface MoreFiltersMenuProps {
  /** Overflowed filter descriptors, in the same order they would appear inline. */
  descriptors: FilterDescriptor[];
  /**
   * Ref callback from `useFilterOverflow`. Attached to the wrapper so the overflow measurement can
   * reserve the "More Filters" control's width.
   */
  registerButton: (element: HTMLElement | null) => void;
}

/**
 * The "More Filters" overflow control. Renders a button that opens a right-hand slide-over
 * panel (the shared {@link FlyoutLink}, which by default enters from the right — `asDrawer` would make it
 * a bottom drawer, so it is intentionally omitted) listing the filters that no longer fit inline.
 * Selections inside the panel apply in real time because each control reads/writes FilterContext
 * exactly as it does inline — no extra wiring here.
 *
 * A count badge shows how many overflowed filters currently have an active selection.
 */
export const MoreFiltersMenu: FC<MoreFiltersMenuProps> = ({ descriptors, registerButton }) => {
  const activeCount = descriptors.filter((d) => d.isActive).length;

  return (
    <div className="network-portfolio-management-more-filters" ref={registerButton}>
      <FlyoutLink
        classNames="network-portfolio-management-more-filters__btn"
        content={{
          body: (
            <div className="network-portfolio-management-more-filters-panel">
              {descriptors.map((descriptor) => (
                <div className="network-portfolio-management-more-filters-panel__item" key={descriptor.id}>
                  {descriptor.render()}
                </div>
              ))}
            </div>
          ),
          subTitle: "Add filter criteria to refine your network search results.",
          title: "More Filters",
        }}
        ignoreDomUpdatesInFlyout
        size="40"
        testId="npm-more-filters"
      >
        <QesIcon iconName={QesIcons.FilterLines} isDecorative width="14" />
        <span className="network-portfolio-management-more-filters__btn-label">More Filters</span>
        {activeCount > 0 && (
          <Badge badgeType="badge--info" className="network-portfolio-management-more-filters__badge">
            {activeCount}
          </Badge>
        )}
      </FlyoutLink>
    </div>
  );
};
